import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import {
  useGetExternalVehiclesQuery,
  useCreateExternalVehicleMutation,
  useUpdateExternalVehicleMutation,
  useDeleteExternalVehicleMutation
} from "../../../api/endpoints/externalVehicle";
import { useLanguage } from "../../../contexts/LanguageContext";
import { toastService } from "../../../utils/toastService";

const useExternalVehicleData = () => {
  const { th } = useLanguage();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });
  const [searchText, setSearchText] = useState("");

  const filterFieldNames = useMemo(() => ["searchText"], []);

  const createInitialFilters = useCallback(() => {
    const f = {};
    for (const field of filterFieldNames) {
      f[field] = {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
      };
    }
    f.global = { value: null, matchMode: FilterMatchMode.CONTAINS };
    return f;
  }, [filterFieldNames]);

  const [filters, setFilters] = useState(createInitialFilters());

  const queryParams = useMemo(
    () => ({
      pageNumber: pagination.PageNumber,
      pageSize: pagination.PageSize,
      searchText: searchText || ""
    }),
    [pagination, searchText]
  );

  const {
    data,
    isLoading: isLoadingList,
    isFetching,
    error: listError,
    refetch
  } = useGetExternalVehiclesQuery(queryParams, {
    refetchOnMountOrArgChange: true
  });

  const [createExternalVehicle, { isLoading: isCreating }] = useCreateExternalVehicleMutation();
  const [updateExternalVehicle, { isLoading: isUpdating }] = useUpdateExternalVehicleMutation();
  const [deleteExternalVehicle, { isLoading: isDeleting }] = useDeleteExternalVehicleMutation();

  useEffect(() => {
    if (data) {
      setRows(Array.isArray(data.items) ? data.items : []);
      setTotalCount(data.totalCount || 0);
    }
  }, [data]);

  useEffect(() => {
    setLoading(isLoadingList || isFetching);
  }, [isLoadingList, isFetching]);

  const handleFilterChange = useCallback(
    (updatedFilters) => {
      if ("searchText" in updatedFilters) {
        setSearchText(updatedFilters.searchText || "");
      }
      setPagination((p) => ({ ...p, PageNumber: 1 }));
      setTimeout(() => refetch(), 50);
    },
    [refetch]
  );

  const handleApplyFilters = useCallback(
    (updated) => handleFilterChange(updated),
    [handleFilterChange]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setSearchText("");
    setPagination((p) => ({ ...p, PageNumber: 1 }));
    setTimeout(() => refetch(), 50);
  }, [createInitialFilters, refetch]);

  const handleSearch = useCallback(
    (text) => handleFilterChange({ searchText: text || "" }),
    [handleFilterChange]
  );

  const onPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination((prev) => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows
    }));
  }, []);

  const handleDelete = async (row) => {
    try {
      await deleteExternalVehicle({ id: row.id }).unwrap();
      toastService.success(th("externalVehicle.deleteSuccess"));
      refetch();
    } catch (e) {
      toastService.error(th("externalVehicle.deleteError"));
    }
  };

  const saveVehicle = async (payload, isEdit, current) => {
    try {
      if (isEdit && current) {
        await updateExternalVehicle(payload).unwrap();
        toastService.success(th("externalVehicle.updateSuccess"));
      } else {
        await createExternalVehicle(payload).unwrap();
        toastService.success(th("externalVehicle.createSuccess"));
      }
      refetch();
      return true;
    } catch (e) {
      console.error(e);
      toastService.error(th("externalVehicle.saveError"));
      return false;
    }
  };

  const filterFields = useMemo(
    () => [
      {
        field: "searchText",
        headerName: th("externalVehicle.searchPlaceholder"),
        filterPlaceholder: th("externalVehicle.searchPlaceholder"),
        type: "text"
      }
    ],
    [th]
  );

  const customFilterTypes = useMemo(
    () => ({
      searchText: "text"
    }),
    []
  );

  const customFilterOptions = useMemo(() => ({}), []);

  return {
    rows,
    loading,
    totalCount,
    pagination,
    filters,
    searchText,
    isCreating,
    isUpdating,
    isDeleting,
    filterFields,
    customFilterTypes,
    customFilterOptions,
    handleDelete,
    saveVehicle,
    handleSearch,
    handleFilterChange,
    handleResetFilters,
    handleApplyFilters,
    onPageChange
  };
};

export default useExternalVehicleData;

